// Phase 1C FINAL — stats UI state reducer.
//
// Preserves the last known-good stats snapshot across refresh failures.
// Chain mismatch is a hard error and suppresses viewer data downstream.
import type { DataStatus, StatsBlock } from "./types";

export interface StatsData {
  data: StatsBlock;
  chain_id: number;
  reconciled: boolean;
  data_status: DataStatus;
}

export interface StatsUiState {
  lastGood: StatsData | null;
  loading: boolean;
  refreshError: string | null;
  chainMismatch: { got: number; expected: number } | null;
}

export type StatsEvent =
  | { type: "loading-start" }
  | { type: "success"; data: StatsData }
  | { type: "failure"; message: string }
  | { type: "chain-mismatch"; got: number; expected: number };

export function initStatsState(): StatsUiState {
  return { lastGood: null, loading: true, refreshError: null, chainMismatch: null };
}

export function nextStatsState(prev: StatsUiState, ev: StatsEvent): StatsUiState {
  switch (ev.type) {
    case "loading-start":
      return { ...prev, loading: true };
    case "success":
      // Any prior refreshError or chainMismatch clears on success.
      return {
        lastGood: ev.data,
        loading: false,
        refreshError: null,
        chainMismatch: null,
      };
    case "failure":
      // Preserve lastGood; surface explicit refreshError.
      return { ...prev, loading: false, refreshError: ev.message };
    case "chain-mismatch":
      // Preserve lastGood but mark chain mismatch — downstream treats as hard.
      return {
        ...prev,
        loading: false,
        chainMismatch: { got: ev.got, expected: ev.expected },
      };
  }
}
